import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, ExternalLink, Eye, Globe, TrendingUp, TrendingDown, Download } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const empty = {
  type: "blog", title: "", excerpt: "", content: "", cover_image: "", category: "",
  published: true, production: "", venue: "", rating: "", show_date: "",
};

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  const load = useCallback(() => {
    api.get("/posts", { params: { admin: true } }).then((r) => setPosts(r.data)).catch(() => {});
    api.get("/contact").then((r) => setMessages(r.data)).catch(() => {});
    api.get("/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const openCreate = (type) => {
    setEditing(null);
    setForm({ ...empty, type });
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...empty, ...p, rating: p.rating ?? "" });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    setSaving(true);
    const payload = { ...form, rating: form.rating === "" ? null : Number(form.rating) };
    try {
      if (editing) {
        await api.put(`/posts/${editing.id}`, payload);
        toast.success("Post updated");
      } else {
        await api.post("/posts", payload);
        toast.success("Post created");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try {
      await api.delete(`/posts/${p.id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const exportCsv = async (type) => {
    try {
      const res = await api.get("/analytics/export", {
        params: type ? { post_type: type } : {},
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = type ? `stagevoice-views-${type}.csv` : "stagevoice-views.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch {
      toast.error("Export failed");
    }
  };

  if (loading || !user) {
    return <div className="min-h-screen flex items-center justify-center text-[#737373]">Loading…</div>;
  }

  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="font-serif-display text-2xl text-[#1A1A1A]">Jack Pritchard · Admin</h1>
            <p className="text-xs text-gray-500">{user.email}</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" data-testid="view-site-button" onClick={() => navigate("/")}>
              <ExternalLink size={15} className="mr-1" /> View Site
            </Button>
            <Button variant="ghost" size="sm" data-testid="logout-button" onClick={handleLogout}>
              <LogOut size={15} className="mr-1" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <Tabs defaultValue="posts">
          <TabsList data-testid="admin-tabs">
            <TabsTrigger value="posts" data-testid="tab-posts">Content</TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-messages">Messages ({messages.length})</TabsTrigger>
            <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>
          </TabsList>

          <TabsContent value="posts" className="mt-6">
            <div className="flex gap-3 mb-5">
              <Button data-testid="new-review-button" onClick={() => openCreate("review")}>
                <Plus size={16} className="mr-1" /> New Review
              </Button>
              <Button variant="outline" data-testid="new-blog-button" onClick={() => openCreate("blog")}>
                <Plus size={16} className="mr-1" /> New Article
              </Button>
            </div>
            <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
              <Table data-testid="posts-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Views</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {posts.map((p) => (
                    <TableRow key={p.id} data-testid={`post-row-${p.id}`}>
                      <TableCell className="font-medium">{p.title}</TableCell>
                      <TableCell><Badge variant={p.type === "review" ? "default" : "secondary"}>{p.type}</Badge></TableCell>
                      <TableCell className="text-gray-500">{p.category || "—"}</TableCell>
                      <TableCell className="text-gray-700 font-medium">{p.views ?? 0}</TableCell>
                      <TableCell>{p.published ? <span className="text-green-600 text-sm">Published</span> : <span className="text-gray-400 text-sm">Draft</span>}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" data-testid={`edit-${p.id}`} onClick={() => openEdit(p)}><Pencil size={15} /></Button>
                        <Button variant="ghost" size="icon" data-testid={`delete-${p.id}`} onClick={() => remove(p)}><Trash2 size={15} className="text-red-500" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {posts.length === 0 && (
                    <TableRow><TableCell colSpan={6} className="text-center text-gray-400 py-8">No content yet.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="messages" className="mt-6">
            <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
              <Table data-testid="messages-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Service</TableHead>
                    <TableHead>Preferred date</TableHead>
                    <TableHead>Message</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {messages.map((m) => (
                    <TableRow key={m.id} data-testid={`message-row-${m.id}`}>
                      <TableCell className="font-medium">{m.name}</TableCell>
                      <TableCell className="text-sm text-gray-600">{m.email}<br />{m.phone}</TableCell>
                      <TableCell>{m.service || "—"}</TableCell>
                      <TableCell>{m.preferred_date || "—"}</TableCell>
                      <TableCell className="max-w-xs text-sm text-gray-600">{m.message}</TableCell>
                    </TableRow>
                  ))}
                  {messages.length === 0 && (
                    <TableRow><TableCell colSpan={5} className="text-center text-gray-400 py-8">No messages yet.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="analytics" className="mt-6">
            {!analytics ? (
              <p className="text-gray-400 py-8">Loading analytics…</p>
            ) : analytics.total_views === 0 ? (
              <p className="text-gray-400 py-8">No views recorded yet. Once people read your posts, detailed stats will appear here.</p>
            ) : (
              <div className="space-y-8" data-testid="analytics-panel">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <p className="text-sm text-gray-500">Detailed reach for your reviews and articles</p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" data-testid="export-all-csv" onClick={() => exportCsv(null)}>
                      <Download size={15} className="mr-1" /> Export all (CSV)
                    </Button>
                    <Button variant="outline" size="sm" data-testid="export-reviews-csv" onClick={() => exportCsv("review")}>
                      <Download size={15} className="mr-1" /> Reviews only
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                  <div className="bg-white border border-gray-200 rounded-md p-6">
                    <div className="flex items-center gap-2 text-gray-500 text-sm mb-1"><Eye size={16} /> Total views</div>
                    <p className="text-3xl font-semibold text-[#1A1A1A]">{analytics.total_views}</p>
                  </div>
                  <div className="bg-white border border-gray-200 rounded-md p-6">
                    <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">This month</div>
                    <p className="text-3xl font-semibold text-[#1A1A1A]">{analytics.this_month}</p>
                    <div className={`flex items-center gap-1 text-xs mt-1 ${analytics.change_pct >= 0 ? "text-green-600" : "text-red-500"}`}>
                      {analytics.change_pct >= 0 ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
                      {analytics.change_pct >= 0 ? "+" : ""}{analytics.change_pct}% vs last month ({analytics.last_month})
                    </div>
                  </div>
                  <div className="bg-white border border-gray-200 rounded-md p-6">
                    <div className="flex items-center gap-2 text-gray-500 text-sm mb-1"><Globe size={16} /> Countries</div>
                    <p className="text-3xl font-semibold text-[#1A1A1A]">{analytics.top_countries.filter(c => c.name).length}</p>
                  </div>
                  <div className="bg-white border border-gray-200 rounded-md p-6">
                    <div className="flex items-center gap-2 text-gray-500 text-sm mb-1"><Eye size={16} /> Posts tracked</div>
                    <p className="text-3xl font-semibold text-[#1A1A1A]">{analytics.per_post.length}</p>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-md p-6">
                  <p className="font-medium mb-4 text-[#1A1A1A]">Views over the last 30 days</p>
                  <ResponsiveContainer width="100%" height={260}>
                    <LineChart data={analytics.by_day} margin={{ left: -20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} interval={4} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="views" stroke="#7A1C28" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
                    <p className="font-medium p-4 border-b border-gray-100">Top posts</p>
                    <Table>
                      <TableBody>
                        {analytics.per_post.map((p, i) => (
                          <TableRow key={i}>
                            <TableCell className="text-sm">{p.name}</TableCell>
                            <TableCell className="text-right font-medium">{p.views}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
                    <p className="font-medium p-4 border-b border-gray-100">Top locations</p>
                    <Table>
                      <TableBody>
                        {analytics.top_cities.filter(c => c.name).length === 0 ? (
                          <TableRow><TableCell className="text-sm text-gray-400 py-6">Location data will appear as views come in.</TableCell></TableRow>
                        ) : analytics.top_cities.filter(c => c.name).map((c, i) => (
                          <TableRow key={i}>
                            <TableCell className="text-sm">{c.name}</TableCell>
                            <TableCell className="text-right font-medium">{c.views}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
                  <p className="font-medium p-4 border-b border-gray-100">Recent views</p>
                  <Table data-testid="recent-views-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date &amp; time</TableHead>
                        <TableHead>Post</TableHead>
                        <TableHead>Location</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {analytics.recent.map((r, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-sm text-gray-600">{r.timestamp ? new Date(r.timestamp).toLocaleString("en-GB") : "—"}</TableCell>
                          <TableCell className="text-sm">{r.title}</TableCell>
                          <TableCell className="text-sm text-gray-600">
                            {[r.city, r.region, r.country].filter(Boolean).join(", ") || "Unknown"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="post-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">
              {editing ? "Edit" : "New"} {form.type === "review" ? "Review" : "Article"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="dialog-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="blog">Blog / Resource Article</SelectItem>
                  <SelectItem value="review">Theatre Review</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Title</Label>
              <Input data-testid="dialog-title-input" value={form.title} onChange={set("title")} placeholder="Post title" />
            </div>
            <div>
              <Label>Category</Label>
              <Input data-testid="dialog-category-input" value={form.category} onChange={set("category")} placeholder="e.g. LAMDA, Shakespeare, Resources" />
            </div>
            <div>
              <Label>Cover image URL</Label>
              <Input data-testid="dialog-image-input" value={form.cover_image} onChange={set("cover_image")} placeholder="https://…" />
            </div>
            {form.type === "review" && (
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Production</Label><Input data-testid="dialog-production-input" value={form.production} onChange={set("production")} /></div>
                <div><Label>Venue</Label><Input data-testid="dialog-venue-input" value={form.venue} onChange={set("venue")} /></div>
                <div><Label>Rating (0–5)</Label><Input data-testid="dialog-rating-input" type="number" step="0.5" min="0" max="5" value={form.rating} onChange={set("rating")} /></div>
                <div><Label>Show date</Label><Input data-testid="dialog-showdate-input" value={form.show_date} onChange={set("show_date")} placeholder="May 2026" /></div>
              </div>
            )}
            <div>
              <Label>Excerpt</Label>
              <Textarea data-testid="dialog-excerpt-input" rows={2} value={form.excerpt} onChange={set("excerpt")} placeholder="Short summary shown on cards" />
            </div>
            <div>
              <Label>Content</Label>
              <Textarea data-testid="dialog-content-input" rows={8} value={form.content} onChange={set("content")} placeholder="Full post content (use blank lines for paragraphs)" />
            </div>
            <div className="flex items-center gap-3">
              <Switch data-testid="dialog-published-switch" checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} />
              <Label>Published</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button data-testid="dialog-save-button" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
